import React, {
  useEffect,
  useState,
  useRef,
  Fragment
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Spinner } from '@blueprintjs/core';
import { FormTemplate } from 'data/template/template.types';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import ProgressDialog from 'components/ProgressDialog';
import { RootState } from 'data/root.reducers';
import { AuditDetails, WithAuditDetails } from 'data/types';
import * as radiusParserPasswordSelectors from 'data/radiusParserPassword/radiusParserPassword.selectors';
import * as radiusParserPasswordActions from 'data/radiusParserPassword/radiusParserPassword.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import ModalDialog from 'shared/modal/dialog';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import {
  RadiusParserPassword,
} from 'data/radiusParserPassword/radiusParserPassword.types';
import RadiusParserPasswordForm from '../../components/RadiusParserPasswordForm';
import { Ref } from '../../components/RadiusParserPasswordForm/RadiusParserPasswordForm';
import { radiusParserPasswordTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
} from '../../utils/radiusParserPasswordTemplate.adapter';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  radiusParserPassword: radiusParserPasswordSelectors.getRadiusParserPassword(state),
  radiusParserPasswordAdaptedTemplate: adaptTemplateVisibilityMode(radiusParserPasswordTemplate, 'view'),
  radiusUiState: radiusParserPasswordSelectors.getRadiusParserPasswordsUIState(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
});

const mapDispatch = {
  goToRadiusParserPasswordEdit: radiusParserPasswordActions.goToRadiusParserPasswordEdit,
  getRadiusParserPassword: radiusParserPasswordActions.getRadiusParserPassword,
  deleteRadiusParserPassword: radiusParserPasswordActions.deleteRadiusParserPassword,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const RadiusParserPasswordView = ({
  history,
  radiusParserPassword,
  radiusParserPasswordAdaptedTemplate,
  radiusUiState,
  isAuditEnabled,
  userName,
  userId,
  getRadiusParserPassword,
  goToRadiusParserPasswordEdit,
  deleteRadiusParserPassword,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openConfirmActionDialog, setOpenConfirmActionDialog] = useState(false);
  const [formData, setFormData] = useState<Partial<RadiusParserPassword>>({});
  const [template, setTemplate] = useState<FormTemplate>(radiusParserPasswordAdaptedTemplate as FormTemplate);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(radiusUiState.isLoading);
  }, [radiusUiState]);

  useEffect(() => {
    if (radiusParserPassword?.id) {
      return;
    }
    getRadiusParserPassword();
  }, [radiusParserPassword, getRadiusParserPassword]);

  useEffect(() => {
    if (!radiusParserPassword?.id || !radiusParserPasswordAdaptedTemplate) {
      return;
    }

    const clonedTemplate = cloneDeep(radiusParserPasswordAdaptedTemplate);
    clonedTemplate.key = Date.now().toString();

    setTemplate(clonedTemplate);
    setFormData(cloneDeep(radiusParserPassword));
  }, [radiusParserPassword, radiusParserPasswordAdaptedTemplate]);

  const handleEdit = () => goToRadiusParserPasswordEdit(history);

  const handleClickDelete = () => {
    setOpenConfirmActionDialog(true);
  };

  const handleClickCancelDelete = () => {
    setOpenConfirmActionDialog(false);
  };

  const handleDelete = () => {

    if (radiusParserPassword == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        radiusParserPassword.id,
        AuditService.RADIUS_PARSER_PASSWORD,
        AuditActionType.RADIUS_PARSER_PASSWORD_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteRadiusParserPassword(radiusParserPassword.id, body, history);

  };

  const renderForm = () => {
    if (template) {
      return (
        <RadiusParserPasswordForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing={false}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('radiusParserPassword.view.title')}>
        <FormButtons
          isEditing={false}
          onClickEdit={handleEdit}
          onClickDelete={handleClickDelete}
        />
      </FormPanel>
    );
  };

  if (loading) {
    return <Spinner aria-label="Loading..." />;
  }

  if (!radiusParserPassword?.id) {
    return <div />;
  }

  return (
    <div data-testid="RadiusParserPasswordView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <ModalDialog
        width="500px"
        isOpen={openConfirmActionDialog}
        title={t('radiusParserPassword.delete.title')}
        customComponent={() => (
          <Fragment>
            {t('radiusParserPassword.delete.message')}
          </Fragment>
        )}
        actionText={t('common.button.confirmDelete')}
        onSubmit={handleDelete}
        onClose={handleClickCancelDelete}
      />
      <ProgressDialog
        open={radiusUiState.isSaving}
        title={t('radiusParserPassword.edit.deleting')}
      />
    </div>
  );
};

export default withRouter(connector(RadiusParserPasswordView));
